import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { SiMicrosoftteams, SiGitlab } from "react-icons/si";
import JiraSetupGuideModal from '../../modals/JiraSetupGuideModal';
import {
    getMyCompanyIntegrations,
    updateMyCompanyIntegrations,
    CompanyIntegrationsPayload
} from "../../../utils/api/endpoints/companies";

// ── Icon components ────────────────────────────────────────────────────────

const SlackIcon = () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
        <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zm2.521-10.123a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
    </svg>
);

const JiraIcon = () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
        <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.004-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.215 5.215 0 0 0 5.215 5.214V6.762a1.005 1.005 0 0 0-1.001-1.005zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24.018 12.49V1.005A1.001 1.001 0 0 0 23.013 0z" />
    </svg>
);


const CheckIcon = () => (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
);

const EyeIcon = ({ show }: { show: boolean }) => show ? (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0zm-3-9C7 3 2 12 2 12s5 9 10 9 10-9 10-9S17 3 12 3z" />
    </svg>
) : (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-10-9-10-9a18.02 18.02 0 014.446-5.144M9.88 9.88A3 3 0 0114.12 14.12M3 3l18 18" />
    </svg>
);

// ── Types ──────────────────────────────────────────────────────────────────

type SlackState = { webhook: string };
type JiraState = {
    url: string;
    email: string;
    apiToken: string;
    projectKey: string;
};

// ── Badge ──────────────────────────────────────────────────────────────────

const StatusBadge = ({ connected }: { connected: boolean }) => (
    <span
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider border ${connected
            ? "bg-green-50 text-green-700 border-green-200"
            : "bg-gray-50 text-gray-500 border-gray-200"
            }`}
    >
        <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
        {connected ? "Active" : "Not Configured"}
    </span>
);

// ── Input ──────────────────────────────────────────────────────────────────

interface InputFieldProps {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    type?: string;
    hint?: string;
    disabled?: boolean;
    rightElement?: React.ReactNode;
    autoComplete?: string;
}

const InputField: React.FC<InputFieldProps> = ({
    label, value, onChange, placeholder = "", type = "text", hint, disabled, rightElement, autoComplete
}) => (
    <div>
        <label className="block text-xs font-bold text-gray-700 uppercase tracking-tight mb-1.5">{label}</label>
        <div className="relative">
            <input
                type={type}
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                autoComplete={autoComplete}
                className="w-full bg-white border border-gray-200 text-gray-900 placeholder-gray-400 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all disabled:bg-gray-50 disabled:text-gray-400"
                style={{ paddingRight: rightElement ? "2.75rem" : undefined }}
            />
            {rightElement && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">{rightElement}</div>
            )}
        </div>
        {hint && <p className="mt-1.5 text-[11px] text-gray-500 leading-relaxed">{hint}</p>}
    </div>
);

interface GuideModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const SlackSetupGuideModal: React.FC<GuideModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600">
                            <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
                                <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zm2.521-10.123a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-md font-bold text-gray-900">Slack Webhook Setup</h2>
                            <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">System Alerts</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="p-6 overflow-y-auto space-y-5 text-sm text-gray-600">
                    <div className="space-y-4">
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-100 text-purple-600 font-bold text-xs">1</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Navigate to Slack Apps</h4>
                                <p className="mt-1 text-xs text-gray-500">Go to the <a href="https://api.slack.com/apps" target="_blank" rel="noreferrer" className="text-purple-600 hover:underline font-semibold">Slack App Console</a>, click <strong>Create New App</strong>, and select <strong>Blank app</strong>.</p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-100 text-purple-600 font-bold text-xs">2</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Enable Incoming Webhooks</h4>
                                <p className="mt-1 text-xs text-gray-500">Under Features in the sidebar, select <strong>Incoming Webhooks</strong> and toggle the switch to <strong>On</strong>.</p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-100 text-purple-600 font-bold text-xs">3</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Add Webhook to Workspace</h4>
                                <p className="mt-1 text-xs text-gray-500">Click <strong>Add New Webhook to Workspace</strong>, authorize access to the target channel, and copy the generated Webhook URL.</p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-100 text-purple-600 font-bold text-xs">4</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Activate in Capture The Bug</h4>
                                <p className="mt-1 text-xs text-gray-500">Paste the URL into the Incoming Webhook URL field, click <strong>Test Connection</strong> to verify, then click <strong>Activate Slack</strong>.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ── Slack Card ────────────────────────────────────────────────────────────

interface SlackCardProps {
    state: SlackState;
    onChange: (s: SlackState) => void;
    webhookConfigured: boolean;
    onSave: (payload: CompanyIntegrationsPayload) => Promise<void>;
    saving: boolean;
}

const SlackCard: React.FC<SlackCardProps> = ({ state, onChange, webhookConfigured, onSave, saving }) => {
    const isConnected = webhookConfigured;
    const [testing, setTesting] = useState(false);
    const [showGuide, setShowGuide] = useState(false);
    const [testSuccess, setTestSuccess] = useState(false);
    const [showWebhook, setShowWebhook] = useState(false);

    const handleTest = async () => {
        if (!state.webhook) {
            toast.error("Please enter a Slack webhook URL first");
            return;
        }
        setTesting(true);
        try {
            const axios = (await import("../../../utils/api/axios")).default;
            const res = await axios.post("/v2/companies/my-company/integrations/validate", {
                provider: "slack",
                webhook_url: state.webhook
            });
            if (res.data.success) {
                toast.success("Slack connection verified!");
                setTestSuccess(true);
            } else {
                toast.error("Slack connection failed: " + res.data.message);
                setTestSuccess(false);
            }
        } catch (err: any) {
            toast.error("Connection failed");
            setTestSuccess(false);
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5 transition-all hover:border-purple-200 hover:shadow-xl hover:shadow-purple-500/5">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-50 text-purple-600 ring-1 ring-purple-100">
                        <SlackIcon />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900 text-sm">Slack Integration</h3>
                        <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Webhooks</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setShowGuide(true)}
                        className="flex items-center gap-1.5 text-[11px] font-bold text-purple-600 hover:text-purple-700 transition-colors bg-purple-50 px-2 py-1 rounded-md ring-1 ring-purple-100"
                    >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Guide
                    </button>
                    <StatusBadge connected={isConnected} />
                </div>
            </div>

            {/* Field */}
            <div className="space-y-4 mb-6">
                <InputField
                    label="Incoming Webhook URL"
                    value={state.webhook}
                    onChange={v => {
                        setTestSuccess(false);
                        onChange({ ...state, webhook: v });
                    }}
                    placeholder={
                        isConnected
                            ? "•••••••••••••••••••• (Webhook configured)"
                            : "https://hooks.slack.com/services/..."
                    }
                    type={showWebhook ? "text" : "password"}
                    autoComplete="new-password"
                    hint="Alerts for new reports and retest status updates will be sent to the configured channel."
                    disabled={isConnected}
                    rightElement={
                        !isConnected && (
                            <button
                                type="button"
                                onClick={() => setShowWebhook(s => !s)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                tabIndex={-1}
                            >
                                <EyeIcon show={showWebhook} />
                            </button>
                        )
                    }
                />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center gap-3">
                    {isConnected ? (
                        <button
                            onClick={() => {
                                onChange({ webhook: "" });
                                onSave({ slack_webhook_url: "" });
                                setTestSuccess(false);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 text-xs font-bold transition-all"
                        >
                            Disconnect
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={handleTest}
                                disabled={testing || saving}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs font-bold transition-all disabled:opacity-50"
                            >
                                {testing ? (
                                    <span>Testing...</span>
                                ) : (
                                    <>
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                        <span>Test Connection</span>
                                    </>
                                )}
                            </button>
                            {testSuccess && (
                                <button
                                    onClick={() => onSave({ slack_webhook_url: state.webhook })}
                                    disabled={saving}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold transition-all disabled:opacity-50 shadow-sm"
                                >
                                    {saving ? (
                                        <div className="flex items-center gap-2">
                                            <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                                                <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                                                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83" strokeLinecap="round" />
                                            </svg>
                                            <span>Saving...</span>
                                        </div>
                                    ) : (
                                        <>
                                            <CheckIcon />
                                            <span>Activate Slack</span>
                                        </>
                                    )}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
            <SlackSetupGuideModal isOpen={showGuide} onClose={() => setShowGuide(false)} />
        </div>
    );
};

// ── Jira Card ─────────────────────────────────────────────────────────────

interface JiraCardProps {
    state: JiraState;
    onChange: (s: JiraState) => void;
    apiTokenSet: boolean;
    onSave: (payload: CompanyIntegrationsPayload) => Promise<void>;
    saving: boolean;
}

const JiraCard: React.FC<JiraCardProps> = ({ state, onChange, apiTokenSet, onSave, saving }) => {
    const [showToken, setShowToken] = useState(false);
    const [showGuide, setShowGuide] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<{ success: boolean; message: string; suggestions?: string[] } | null>(null);
    const [testSuccess, setTestSuccess] = useState(false);

    const isConnected = !!state.url && !!state.email && (apiTokenSet || !!state.apiToken);

    const handleTestConnection = async () => {
        if (!state.url || !state.email || (!apiTokenSet && !state.apiToken) || !state.projectKey) {
            toast.error("Please fill in all Jira fields before testing");
            return;
        }

        setTesting(true);
        setTestResult(null);

        try {
            const axios = (await import("../../../utils/api/axios")).default;
            const response = await axios.post("/v2/programs/jira/validate", {
                url: state.url,
                email: state.email,
                api_token: state.apiToken || undefined, // Send if provided, otherwise server uses existing
                project_key: state.projectKey
            });

            setTestResult({
                success: response.data.success,
                message: response.data.error || "Connection successful!",
                suggestions: response.data.suggestions
            });

            if (response.data.success) {
                toast.success("Jira connection verified!");
                setTestSuccess(true);
            } else {
                toast.error("Jira connection failed");
                setTestSuccess(false);
            }
        } catch (error: any) {
            setTestResult({
                success: false,
                message: error.response?.data?.message || "Failed to connect to Jira server"
            });
            toast.error("Error testing connection");
            setTestSuccess(false);
        } finally {
            setTesting(false);
        }
    };

    const handleSave = async () => {
        const payload: CompanyIntegrationsPayload = {
            jira_url: state.url,
            jira_email: state.email,
            jira_project_key: state.projectKey
        };
        if (state.apiToken) {
            payload.jira_api_token = state.apiToken;
        }
        await onSave(payload);
    };

    return (
        <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5 transition-all hover:border-blue-200 hover:shadow-xl hover:shadow-blue-500/5">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-blue-100">
                        <JiraIcon />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900 text-sm">Jira Software</h3>
                        <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Ticketing Sync</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setShowGuide(true)}
                        className="flex items-center gap-1.5 text-[11px] font-bold text-blue-600 hover:text-blue-700 transition-colors bg-blue-50 px-2 py-1 rounded-md ring-1 ring-blue-100"
                    >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Guide
                    </button>
                    <StatusBadge connected={isConnected} />
                </div>
            </div>

            <div className="mb-6">
                <p className="text-xs text-gray-500 leading-relaxed">
                    Automatically create Jira issues for every approved report.
                </p>
            </div>

            {/* Fields */}
            <div className="space-y-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                        label="Atlassian Domain"
                        value={state.url}
                        onChange={v => {
                            setTestSuccess(false);
                            onChange({ ...state, url: v });
                        }}
                        placeholder="yourcompany.atlassian.net"
                        disabled={isConnected}
                    />
                    <InputField
                        label="Project Key"
                        value={state.projectKey}
                        onChange={v => {
                            setTestSuccess(false);
                            onChange({ ...state, projectKey: v.toUpperCase() });
                        }}
                        placeholder="e.g. SEC"
                        disabled={isConnected}
                    />
                </div>
                <InputField
                    label="Account Email"
                    value={state.email}
                    onChange={v => {
                        setTestSuccess(false);
                        onChange({ ...state, email: v });
                    }}
                    placeholder="you@company.com"
                    type="email"
                    disabled={isConnected}
                />
                <InputField
                    label="API Token"
                    value={state.apiToken}
                    onChange={v => {
                        setTestSuccess(false);
                        onChange({ ...state, apiToken: v });
                    }}
                    placeholder={apiTokenSet ? "•••••••• (Token configured)" : "Jira Personal Access Token"}
                    type={showToken ? "text" : "password"}
                    autoComplete="new-password"
                    hint="Securely stored. Used for automated issue creation from reports."
                    disabled={isConnected}
                    rightElement={
                        !isConnected && (
                            <button
                                type="button"
                                onClick={() => setShowToken(s => !s)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                tabIndex={-1}
                            >
                                <EyeIcon show={showToken} />
                            </button>
                        )
                    }
                />
            </div>

            {/* Test Connection Result */}
            {testResult && (
                <div className={`mb-6 p-4 rounded-lg border ${testResult.success ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}>
                    <div className="flex gap-2">
                        <div className="mt-0.5">
                            {testResult.success ? (
                                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            ) : (
                                <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            )}
                        </div>
                        <div>
                            <p className="text-xs font-bold uppercase tracking-tight">{testResult.success ? "Success" : "Connection Failed"}</p>
                            <p className="mt-1 text-xs leading-relaxed">{testResult.message}</p>
                            {testResult.suggestions && testResult.suggestions.length > 0 && (
                                <ul className="mt-2 space-y-1 list-disc list-inside text-[11px] opacity-90">
                                    {testResult.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center gap-3">
                    {isConnected ? (
                        <button
                            onClick={() => {
                                onSave({
                                    jira_url: "",
                                    jira_email: "",
                                    jira_api_token: "",
                                    jira_project_key: ""
                                });
                                onChange({
                                    url: "",
                                    email: "",
                                    apiToken: "",
                                    projectKey: ""
                                });
                                setTestSuccess(false);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 text-xs font-bold transition-all"
                        >
                            Disconnect
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={handleTestConnection}
                                disabled={testing || saving}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs font-bold transition-all disabled:opacity-50"
                            >
                                {testing ? (
                                    <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                                        <path d="M12 2v4M12 18v4" strokeLinecap="round" />
                                    </svg>
                                ) : (
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                )}
                                <span>Test Connection</span>
                            </button>
                            {testSuccess && (
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-all disabled:opacity-50 shadow-sm"
                                >
                                    {saving ? (
                                        <div className="flex items-center gap-2">
                                            <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                                                <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                                                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83" strokeLinecap="round" />
                                            </svg>
                                            <span>Syncing...</span>
                                        </div>
                                    ) : (
                                        <>
                                            <CheckIcon />
                                            <span>Connect Jira</span>
                                        </>
                                    )}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            <JiraSetupGuideModal
                isOpen={showGuide}
                onClose={() => setShowGuide(false)}
            />
        </div>
    );
};

const TeamsIcon = () => <SiMicrosoftteams className="w-6 h-6" />;

const GitHubIcon = () => (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
        <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.464-1.11-1.464-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.579.688.481C19.137 20.162 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
    </svg>
);

// ── Teams Card ────────────────────────────────────────────────────────────

interface TeamsCardProps {
    state: { webhook: string };
    onChange: (s: { webhook: string }) => void;
    initialConnected: boolean;
    onSave: (payload: CompanyIntegrationsPayload) => Promise<void>;
    saving: boolean;
}
const TeamsSetupGuideModal: React.FC<GuideModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                            <TeamsIcon />
                        </div>
                        <div>
                            <h2 className="text-md font-bold text-gray-900">Microsoft Teams Webhook Setup</h2>
                            <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">System Alerts</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="p-6 overflow-y-auto space-y-5 text-sm text-gray-600">
                    <div className="space-y-4">
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 font-bold text-xs">1</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Open Teams Workflows</h4>
                                <p className="mt-1 text-xs text-gray-500">In Microsoft Teams, click the three dots <strong>...</strong> next to your target channel, select <strong>Workflows</strong>, and search for <strong>"webhook"</strong>.</p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 font-bold text-xs">2</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Select Webhook Template</h4>
                                <p className="mt-1 text-xs text-gray-500">Choose the template named <strong>"Send webhook alerts to a channel"</strong>.</p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 font-bold text-xs">3</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Configure & Generate URL</h4>
                                <p className="mt-1 text-xs text-gray-500">Give your workflow a name, click Next/Log In to verify your Teams account, select your target Team and Channel, and click <strong>Create flow</strong> to copy the generated URL.</p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 font-bold text-xs">4</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Verify Connection</h4>
                                <p className="mt-1 text-xs text-gray-500">Paste the URL into the Teams Webhook field, click <strong>Test Connection</strong>, then click <strong>Activate Teams</strong>.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ── Teams Card ────────────────────────────────────────────────────────────

const TeamsCard: React.FC<TeamsCardProps> = ({ state, onChange, initialConnected, onSave, saving }) => {
    const isConnected = initialConnected;
    const [testing, setTesting] = useState(false);
    const [showGuide, setShowGuide] = useState(false);
    const [testSuccess, setTestSuccess] = useState(false);
    const [showWebhook, setShowWebhook] = useState(false);

    const handleTest = async () => {
        if (!state.webhook) {
            toast.error("Please enter a Teams webhook URL first");
            return;
        }
        setTesting(true);
        try {
            const axios = (await import("../../../utils/api/axios")).default;
            const res = await axios.post("/v2/companies/my-company/integrations/validate", {
                provider: "teams",
                webhook_url: state.webhook
            });
            if (res.data.success) {
                toast.success("Teams connection verified!");
                setTestSuccess(true);
            } else {
                toast.error("Teams connection failed: " + res.data.message);
                setTestSuccess(false);
            }
        } catch (err: any) {
            toast.error("Connection failed");
            setTestSuccess(false);
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5 transition-all hover:border-blue-200 hover:shadow-xl hover:shadow-blue-500/5">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-blue-100">
                        <TeamsIcon />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900 text-sm">Microsoft Teams</h3>
                        <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Webhooks</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setShowGuide(true)}
                        className="flex items-center gap-1.5 text-[11px] font-bold text-blue-600 hover:text-blue-700 transition-colors bg-blue-50 px-2 py-1 rounded-md ring-1 ring-blue-100"
                    >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Guide
                    </button>
                    <StatusBadge connected={isConnected} />
                </div>
            </div>

            <div className="space-y-4 mb-6">
                <InputField
                    label="Teams Webhook URL"
                    value={state.webhook}
                    onChange={v => {
                        setTestSuccess(false);
                        onChange({ ...state, webhook: v });
                    }}
                    placeholder={
                        isConnected
                            ? "•••••••••••••••••••• (Webhook configured)"
                            : "https://prod-xx.westus.logic.azure.com:443/workflows/..."
                    }
                    type={showWebhook ? "text" : "password"}
                    autoComplete="new-password"
                    hint="Alerts for vulnerability reports, QA status changes, and final reports will land in Teams."
                    disabled={isConnected}
                    rightElement={
                        !isConnected && (
                            <button
                                type="button"
                                onClick={() => setShowWebhook(s => !s)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                tabIndex={-1}
                            >
                                <EyeIcon show={showWebhook} />
                            </button>
                        )
                    }
                />
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center gap-3">
                    {isConnected ? (
                        <button
                            onClick={() => {
                                onChange({ webhook: "" });
                                onSave({ teams_webhook_url: "" });
                                setTestSuccess(false);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 text-xs font-bold transition-all"
                        >
                            Disconnect
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={handleTest}
                                disabled={testing || saving}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs font-bold transition-all disabled:opacity-50"
                            >
                                {testing ? (
                                    <span>Testing...</span>
                                ) : (
                                    <>
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                        <span>Test Connection</span>
                                    </>
                                )}
                            </button>
                            {testSuccess && (
                                <button
                                    onClick={() => onSave({ teams_webhook_url: state.webhook })}
                                    disabled={saving}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-all disabled:opacity-50 shadow-sm"
                                >
                                    {saving ? (
                                        <span>Saving...</span>
                                    ) : (
                                        <>
                                            <CheckIcon />
                                            <span>Activate Teams</span>
                                        </>
                                    )}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
            <TeamsSetupGuideModal isOpen={showGuide} onClose={() => setShowGuide(false)} />
        </div>
    );
};

// ── GitHub Setup Guide Modal ──────────────────────────────────────────────

const GitHubSetupGuideModal: React.FC<GuideModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center text-gray-800">
                            <GitHubIcon />
                        </div>
                        <div>
                            <h2 className="text-md font-bold text-gray-900">GitHub Service Account Setup</h2>
                            <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Service Accounts</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="p-6 overflow-y-auto space-y-5 text-sm text-gray-600">
                    <div className="space-y-4">
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-800 font-bold text-xs ring-1 ring-gray-300">1</div>
                            <div>
                                <h4 className="font-bold text-gray-950">OAuth Authorization</h4>
                                <p className="mt-1 text-xs text-gray-500">Click the <strong>Authorize</strong> button to connect your GitHub account via OAuth.</p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-800 font-bold text-xs ring-1 ring-gray-300">2</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Grant Permissions</h4>
                                <p className="mt-1 text-xs text-gray-500">Approve and grant the required access scopes to Capture The Bug on GitHub.</p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-800 font-bold text-xs ring-1 ring-gray-300">3</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Repository Path or URL</h4>
                                <p className="mt-1 text-xs text-gray-500">Input your repository path in <code>owner/repo</code> format (e.g. <code>my-org/web-app</code>) or paste the full GitHub repository URL.</p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-800 font-bold text-xs ring-1 ring-gray-300">4</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Verify & Save</h4>
                                <p className="mt-1 text-xs text-gray-500">Click <strong>Test Connection</strong> to verify authorization access, then click <strong>Save Repository</strong>.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const parseGitHubRepo = (val: string) => {
    let clean = val.trim();
    if (clean.includes("github.com") || clean.startsWith("git@") || clean.startsWith("http")) {
        clean = clean.replace(/^(https?:\/\/)?(www\.)?github\.com\/?/i, "");
        clean = clean.replace(/^git@github\.com:/i, "");
        clean = clean.replace(/\.git$/i, "");
        const parts = clean.split("/").filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[0]}/${parts[1]}`;
        }
    }
    return clean;
};

// ── GitHub Card ───────────────────────────────────────────────────────────

interface GitHubCardProps {
    state: { token: string; repo: string };
    onChange: (s: { token: string; repo: string }) => void;
    tokenSet: boolean;
    initialRepo: string | null;
    onSave: (payload: CompanyIntegrationsPayload) => Promise<void>;
    saving: boolean;
}

const GitHubCard: React.FC<GitHubCardProps> = ({ state, onChange, tokenSet, initialRepo, onSave, saving }) => {
    const isConnected = tokenSet && !!initialRepo;
    const [testing, setTesting] = useState(false);
    const [showGuide, setShowGuide] = useState(false);
    const [testSuccess, setTestSuccess] = useState(false);

    const handleOAuthStart = async () => {
        try {
            const axios = (await import("../../../utils/api/axios")).default;
            const res = await axios.get("/v2/companies/my-company/integrations/github/auth");
            if (res.data.success && res.data.auth_url) {
                window.location.href = res.data.auth_url;
            } else {
                toast.error("Failed to retrieve authorization URL");
            }
        } catch (err: any) {
            toast.error("Failed to start GitHub OAuth handshake flow");
        }
    };

    const handleTest = async () => {
        if (!tokenSet || !state.repo) {
            toast.error("Please authorize GitHub and enter a Repository first");
            return;
        }
        setTesting(true);
        try {
            const axios = (await import("../../../utils/api/axios")).default;
            const res = await axios.post("/v2/companies/my-company/integrations/validate", {
                provider: "github",
                github_repo: state.repo
            });
            if (res.data.success) {
                toast.success("GitHub connection verified!");
                setTestSuccess(true);
            } else {
                toast.error("GitHub connection failed: " + res.data.message);
                setTestSuccess(false);
            }
        } catch (err: any) {
            toast.error("Connection failed");
            setTestSuccess(false);
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5 transition-all hover:border-gray-300 hover:shadow-xl hover:shadow-gray-500/5">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-50 text-gray-800 ring-1 ring-gray-200">
                        <GitHubIcon />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900 text-sm">GitHub Service Integration</h3>
                        <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">OAuth Connection</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setShowGuide(true)}
                        className="flex items-center gap-1.5 text-[11px] font-bold text-gray-800 hover:text-gray-900 transition-colors bg-gray-50 px-2 py-1 rounded-md ring-1 ring-gray-200"
                    >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Guide
                    </button>
                    <StatusBadge connected={isConnected} />
                </div>
            </div>

            <div className="space-y-4 mb-6">
                <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <p className="text-xs font-bold text-gray-800">GitHub Authentication</p>
                            <p className="text-[11px] text-gray-500 mt-1">
                                {tokenSet ? "Authorized successfully via OAuth." : "Authorize CTB to access your GitHub repositories securely."}
                            </p>
                        </div>
                        {!isConnected && (
                            <button
                                type="button"
                                onClick={handleOAuthStart}
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-2xs border ${
                                    tokenSet
                                        ? "bg-white text-gray-700 hover:bg-gray-50 border-gray-200"
                                        : "bg-gray-900 text-white hover:bg-gray-800 border-transparent"
                                }`}
                            >
                                <GitHubIcon />
                                <span>{tokenSet ? "Reconnect" : "Authorize"}</span>
                            </button>
                        )}
                    </div>
                </div>

                <InputField
                    label="GitHub Repository (owner/repo)"
                    value={state.repo}
                    onChange={v => {
                        setTestSuccess(false);
                        onChange({ ...state, repo: parseGitHubRepo(v) });
                    }}
                    placeholder="my-organization/my-repository"
                    disabled={!tokenSet || isConnected}
                />
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center gap-3">
                    {isConnected ? (
                        <button
                            onClick={() => {
                                onSave({ github_repo: "" });
                                onChange({ token: "", repo: "" });
                                setTestSuccess(false);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 text-xs font-bold transition-all"
                        >
                            Disconnect
                        </button>
                    ) : (
                        <>
                            {tokenSet && (
                                <button
                                    type="button"
                                    onClick={handleTest}
                                    disabled={testing || saving || !state.repo}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs font-bold transition-all disabled:opacity-50"
                                >
                                    {testing ? (
                                        <span>Testing...</span>
                                    ) : (
                                        <>
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                            <span>Test Connection</span>
                                        </>
                                    )}
                                </button>
                            )}
                            {testSuccess && (
                                <button
                                    onClick={() => onSave({ github_repo: state.repo })}
                                    disabled={saving || !tokenSet}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 hover:bg-gray-800 text-white text-xs font-bold transition-all disabled:opacity-50 shadow-sm"
                                >
                                    {saving ? (
                                        <span>Saving...</span>
                                    ) : (
                                        <>
                                            <CheckIcon />
                                            <span>Save Repository</span>
                                        </>
                                    )}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
            <GitHubSetupGuideModal isOpen={showGuide} onClose={() => setShowGuide(false)} />
        </div>
    );
};

// ── GitLab Setup Guide Modal ──────────────────────────────────────────────

const GitLabIcon = () => <SiGitlab className="w-6 h-6 text-[#FC6D26]" />;

const GitLabSetupGuideModal: React.FC<GuideModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                            <GitLabIcon />
                        </div>
                        <div>
                            <h2 className="text-md font-bold text-gray-900">GitLab Integration Setup</h2>
                            <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">No Org Account Required</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="p-6 overflow-y-auto space-y-5 text-sm text-gray-600">
                    <div className="rounded-lg bg-orange-50/60 border border-orange-100 p-3.5 text-xs text-orange-900">
                        <p className="font-semibold mb-1">🚀 Individual / Personal Account Ready</p>
                        <p className="text-orange-800 leading-relaxed">
                            You do not need a GitLab Organization or Group account. Any standard GitLab account can connect via 1-Click OAuth or via a Personal Access Token.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-800 font-bold text-xs ring-1 ring-orange-200">1</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Option A: 1-Click OAuth (Recommended)</h4>
                                <p className="mt-1 text-xs text-gray-500">
                                    Click the <strong>Authorize with GitLab</strong> button below. GitLab will ask you to approve Capture The Bug to access your projects.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-800 font-bold text-xs ring-1 ring-orange-200">2</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Option B: Personal Access Token (PAT)</h4>
                                <p className="mt-1 text-xs text-gray-500">
                                    Alternatively, navigate to <a href="https://gitlab.com/-/user_settings/personal_access_tokens" target="_blank" rel="noreferrer" className="text-orange-600 underline font-semibold">User Settings &gt; Access Tokens</a>, generate a token with the <strong>api</strong> scope, and paste it directly.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-800 font-bold text-xs ring-1 ring-orange-200">3</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Specify Project Path or ID</h4>
                                <p className="mt-1 text-xs text-gray-500">
                                    Enter your repository path in <code>owner/project</code> or <code>group/subgroup/project</code> format (e.g. <code>my-org/backend-service</code>), or paste the full GitLab URL.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-800 font-bold text-xs ring-1 ring-orange-200">4</div>
                            <div>
                                <h4 className="font-bold text-gray-950">Test & Activate</h4>
                                <p className="mt-1 text-xs text-gray-500">
                                    Click <strong>Test Connection</strong> to confirm permissions, then click <strong>Save Project</strong>. Approved vulnerability reports will automatically file GitLab issues.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const parseGitLabProject = (val: string) => {
    let clean = val.trim();
    if (clean.includes("gitlab.com") || clean.startsWith("git@") || clean.startsWith("http")) {
        clean = clean.replace(/^(https?:\/\/)?(www\.)?gitlab\.com\/?/i, "");
        clean = clean.replace(/^git@gitlab\.com:/i, "");
        clean = clean.replace(/\.git$/i, "");
        const parts = clean.split("/").filter(Boolean);
        if (parts.length >= 2) {
            return parts.join("/");
        }
    }
    return clean;
};

// ── GitLab Card ───────────────────────────────────────────────────────────

interface GitLabCardProps {
    state: { token: string; project: string; url: string };
    onChange: (s: { token: string; project: string; url: string }) => void;
    tokenSet: boolean;
    initialProject: string | null;
    onSave: (payload: CompanyIntegrationsPayload) => Promise<void>;
    saving: boolean;
}

const GitLabCard: React.FC<GitLabCardProps> = ({ state, onChange, tokenSet, initialProject, onSave, saving }) => {
    const isConnected = tokenSet && !!initialProject;
    const [testing, setTesting] = useState(false);
    const [showGuide, setShowGuide] = useState(false);
    const [manualTokenMode, setManualTokenMode] = useState(false);
    const [showToken, setShowToken] = useState(false);
    const [showSelfHosted, setShowSelfHosted] = useState(false);

    const handleOAuthStart = async () => {
        try {
            const axios = (await import("../../../utils/api/axios")).default;
            const res = await axios.get("/v2/companies/my-company/integrations/gitlab/auth");
            if (res.data.success && res.data.auth_url) {
                window.location.href = res.data.auth_url;
            } else {
                toast.error("Failed to retrieve authorization URL");
            }
        } catch (err: any) {
            toast.error("Failed to start GitLab OAuth handshake flow");
        }
    };

    const handleTest = async () => {
        const hasAuth = tokenSet || !!state.token;
        if (!hasAuth || !state.project) {
            toast.error("Please authorize GitLab (or provide a token) and enter a Project first");
            return;
        }
        setTesting(true);
        try {
            const axios = (await import("../../../utils/api/axios")).default;
            const res = await axios.post("/v2/companies/my-company/integrations/validate", {
                provider: "gitlab",
                gitlab_project: state.project,
                gitlab_token: state.token || undefined,
                gitlab_url: state.url || undefined
            });
            if (res.data.success) {
                const payload: CompanyIntegrationsPayload = {
                    gitlab_project: state.project,
                    gitlab_url: state.url || undefined
                };
                if (state.token) {
                    payload.gitlab_token = state.token;
                }
                await onSave(payload);
                toast.success("GitLab connection verified & project saved!");
            } else {
                toast.error("GitLab connection failed: " + (res.data.message || "Validation failed"));
            }
        } catch (err: any) {
            toast.error("Connection failed: " + (err.response?.data?.message || err.message));
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5 transition-all hover:border-orange-200 hover:shadow-xl hover:shadow-orange-500/5">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-50 ring-1 ring-orange-200">
                        <GitLabIcon />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900 text-sm">GitLab Service Integration</h3>
                        <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Issue Tracker Sync</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setShowGuide(true)}
                        className="flex items-center gap-1.5 text-[11px] font-bold text-orange-700 hover:text-orange-800 transition-colors bg-orange-50 px-2 py-1 rounded-md ring-1 ring-orange-200"
                    >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Guide
                    </button>
                    <StatusBadge connected={isConnected} />
                </div>
            </div>

            <div className="space-y-4 mb-6">
                <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <p className="text-xs font-bold text-gray-800">GitLab Authentication</p>
                            <p className="text-[11px] text-gray-500 mt-1">
                                {tokenSet
                                    ? "Authorized successfully."
                                    : "Connect via 1-Click OAuth or supply a Personal Access Token."}
                            </p>
                        </div>
                        {!isConnected && (
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleOAuthStart}
                                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-2xs border ${
                                        tokenSet
                                            ? "bg-white text-gray-700 hover:bg-gray-50 border-gray-200"
                                            : "bg-[#FC6D26] text-white hover:bg-[#e0591b] border-transparent"
                                    }`}
                                >
                                    <GitLabIcon />
                                    <span>{tokenSet ? "Reconnect" : "Authorize with GitLab"}</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {!isConnected && (
                        <div className="mt-3 pt-3 border-t border-gray-200/60 flex items-center justify-between text-xs">
                            <button
                                type="button"
                                onClick={() => setManualTokenMode(m => !m)}
                                className="text-orange-600 hover:text-orange-700 font-semibold hover:underline"
                            >
                                {manualTokenMode ? "Hide Token Input" : "Or use Personal Access Token directly"}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowSelfHosted(s => !s)}
                                className="text-gray-500 hover:text-gray-700 font-medium hover:underline text-[11px]"
                            >
                                {showSelfHosted ? "Hide Host URL" : "Self-hosted GitLab?"}
                            </button>
                        </div>
                    )}
                </div>

                {manualTokenMode && !isConnected && (
                    <InputField
                        label="GitLab Personal / Project Access Token"
                        value={state.token}
                        onChange={v => {
                            onChange({ ...state, token: v });
                        }}
                        placeholder="glpat-xxxxxxxxxxxxxxxxxxxx"
                        type={showToken ? "text" : "password"}
                        autoComplete="new-password"
                        hint="Token requires the 'api' scope to create issues and post comment notes."
                        rightElement={
                            <button
                                type="button"
                                onClick={() => setShowToken(s => !s)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                tabIndex={-1}
                            >
                                <EyeIcon show={showToken} />
                            </button>
                        }
                    />
                )}

                {showSelfHosted && (
                    <InputField
                        label="GitLab Host URL (Optional)"
                        value={state.url}
                        onChange={v => {
                            onChange({ ...state, url: v });
                        }}
                        placeholder="https://gitlab.yourcompany.com"
                        hint="Defaults to https://gitlab.com if left blank."
                        disabled={isConnected}
                    />
                )}

                <InputField
                    label="GitLab Project (namespace/project or ID)"
                    value={state.project}
                    onChange={v => {
                        onChange({ ...state, project: parseGitLabProject(v) });
                    }}
                    placeholder="my-organization/my-project"
                    hint="Accepts owner/repo, group/subgroup/project, or numeric Project ID."
                    disabled={!tokenSet && !state.token}
                />
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center gap-3">
                    {isConnected ? (
                        <>
                            <button
                                type="button"
                                onClick={handleTest}
                                disabled={testing || saving || !state.project}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs font-bold transition-all disabled:opacity-50"
                            >
                                {testing || saving ? (
                                    <span>Verifying & Saving...</span>
                                ) : (
                                    <>
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                        <span>{state.project !== initialProject ? "Test & Save Project" : "Test Connection"}</span>
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => {
                                    onSave({ gitlab_project: "", gitlab_token: "", gitlab_url: "" });
                                    onChange({ token: "", project: "", url: "" });
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 text-xs font-bold transition-all"
                            >
                                Disconnect
                            </button>
                        </>
                    ) : (
                        <>
                            {(tokenSet || state.token) && (
                                <button
                                    type="button"
                                    onClick={handleTest}
                                    disabled={testing || saving || !state.project}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold transition-all disabled:opacity-50 shadow-sm"
                                >
                                    {testing || saving ? (
                                        <span>Verifying & Saving...</span>
                                    ) : (
                                        <>
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                            <span>Test & Save Project</span>
                                        </>
                                    )}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
            <GitLabSetupGuideModal isOpen={showGuide} onClose={() => setShowGuide(false)} />
        </div>
    );
};


// ── OAuth Error Sanitization (CWE-74 / CWE-451 Mitigation) ─────────────────

const OAUTH_ERROR_CODE_MAP: Record<string, string> = {
    missing_credentials: "Authentication request was incomplete or missing required parameters.",
    csrf_failed: "Security validation failed. Access denied.",
    access_denied: "Authorization request was cancelled or access was denied.",
    oauth_failed: "Authorization could not be completed with the provider.",
    invalid_token: "Invalid or expired authorization credentials.",
    server_error: "An internal server error occurred during authorization."
};

const ALLOWED_STATIC_ERROR_MESSAGES = new Set([
    "Missing callback credentials",
    "Missing code or state",
    "CSRF State validation failed. Access denied.",
    "Failed to authorize GitHub",
    "Failed to authorize GitLab",
    "Failed to authorize Jira"
]);

const resolveSafeOAuthError = (serviceName: string, params: URLSearchParams): string => {
    const code = params.get("error_code") || params.get("code") || params.get("error");
    if (code && OAUTH_ERROR_CODE_MAP[code]) {
        return OAUTH_ERROR_CODE_MAP[code];
    }
    const rawMsg = params.get("message");
    if (rawMsg && ALLOWED_STATIC_ERROR_MESSAGES.has(rawMsg)) {
        return rawMsg;
    }
    return `Failed to authorize ${serviceName}. Please verify your configuration and try again.`;
};

// ── Main Component ────────────────────────────────────────────────────────

const CompanyIntegrations: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [slackSaving, setSlackSaving] = useState(false);
    const [jiraSaving, setJiraSaving] = useState(false);
    const [teamsSaving, setTeamsSaving] = useState(false);
    const [githubSaving, setGithubSaving] = useState(false);
    const [gitlabSaving, setGitlabSaving] = useState(false);

    const [slackState, setSlackState] = useState<SlackState>({ webhook: "" });
    const [jiraState, setJiraState] = useState<JiraState>({
        url: "",
        email: "",
        apiToken: "",
        projectKey: ""
    });
    const [teamsState, setTeamsState] = useState<{ webhook: string }>({
        webhook: ""
    });
    const [githubState, setGithubState] = useState<{ token: string; repo: string }>({
        token: "",
        repo: ""
    });
    const [gitlabState, setGitlabState] = useState<{ token: string; project: string; url: string }>({
        token: "",
        project: "",
        url: ""
    });

    const [slackWebhookConfigured, setSlackWebhookConfigured] = useState(false);
    const [teamsWebhookConfigured, setTeamsWebhookConfigured] = useState(false);
    const [initialGithubRepo, setInitialGithubRepo] = useState<string | null>(null);
    const [initialGitLabProject, setInitialGitLabProject] = useState<string | null>(null);
    const [apiTokenSet, setApiTokenSet] = useState(false);
    const [githubTokenSet, setGithubTokenSet] = useState(false);
    const [gitlabTokenSet, setGitlabTokenSet] = useState(false);

    /* Load current settings */
    useEffect(() => {
        let cancelled = false;

        const params = new URLSearchParams(window.location.search);
        if (params.has("github")) {
            const status = params.get("github");
            if (status === "success") {
                toast.success("GitHub OAuth authentication successful!");
            } else {
                const msg = resolveSafeOAuthError("GitHub", params);
                toast.error(`GitHub OAuth error: ${msg}`);
            }
            window.history.replaceState({}, document.title, window.location.pathname);
        }

        if (params.has("gitlab")) {
            const status = params.get("gitlab");
            if (status === "success") {
                toast.success("GitLab OAuth authentication successful!");
            } else {
                const msg = resolveSafeOAuthError("GitLab", params);
                toast.error(`GitLab OAuth error: ${msg}`);
            }
            window.history.replaceState({}, document.title, window.location.pathname);
        }

        if (params.has("jira")) {
            const status = params.get("jira");
            if (status === "success") {
                toast.success("Jira OAuth authentication successful!");
            } else {
                const msg = resolveSafeOAuthError("Jira", params);
                toast.error(`Jira OAuth error: ${msg}`);
            }
            window.history.replaceState({}, document.title, window.location.pathname);
        }

        (async () => {
            const data = await getMyCompanyIntegrations();
            if (cancelled) return;
            if (data) {
                setSlackWebhookConfigured(data.slack_webhook_configured);
                setSlackState({ webhook: "" });
                setTeamsWebhookConfigured(data.teams_webhook_configured);
                setTeamsState({ webhook: "" });
                setJiraState({
                    url: data.jira_url || "",
                    email: data.jira_email || "",
                    apiToken: "",
                    projectKey: data.jira_project_key || ""
                });
                setApiTokenSet(data.jira_api_token_set);
                setGithubState({
                    token: "",
                    repo: data.github_repo || ""
                });
                setGithubTokenSet(data.github_token_set);
                setInitialGithubRepo(data.github_repo || null);
                setGitlabState({
                    token: "",
                    project: data.gitlab_project || "",
                    url: data.gitlab_url || ""
                });
                setGitlabTokenSet(data.gitlab_token_set);
                setInitialGitLabProject(data.gitlab_project || null);
            }
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, []);

    const handleSave = useCallback(
        async (payload: CompanyIntegrationsPayload, kind: "slack" | "jira" | "teams" | "github" | "gitlab") => {
            let setSaving;
            if (kind === "slack") setSaving = setSlackSaving;
            else if (kind === "jira") setSaving = setJiraSaving;
            else if (kind === "teams") setSaving = setTeamsSaving;
            else if (kind === "github") setSaving = setGithubSaving;
            else setSaving = setGitlabSaving;

            setSaving(true);
            const result = await updateMyCompanyIntegrations(payload);
            setSaving(false);

            if (result.success) {
                toast.success("Integration settings saved!");
                if (kind === "slack") {
                    setSlackWebhookConfigured(!!payload.slack_webhook_url);
                    if (!payload.slack_webhook_url) {
                        setSlackState({ webhook: "" });
                    }
                }
                if (kind === "teams") {
                    setTeamsWebhookConfigured(!!payload.teams_webhook_url);
                    if (!payload.teams_webhook_url) {
                        setTeamsState({ webhook: "" });
                    }
                }
                if (kind === "jira" && payload.jira_api_token !== undefined) {
                    setApiTokenSet(!!payload.jira_api_token);
                }
                if (kind === "github") {
                    if (payload.github_token !== undefined) {
                        setGithubTokenSet(!!payload.github_token);
                    }
                    if (payload.github_repo !== undefined) {
                        setInitialGithubRepo(payload.github_repo || null);
                    }
                }
                if (kind === "gitlab") {
                    if (payload.gitlab_token !== undefined) {
                        setGitlabTokenSet(!!payload.gitlab_token);
                    }
                    if (payload.gitlab_project !== undefined) {
                        setInitialGitLabProject(payload.gitlab_project || null);
                    }
                }
            } else {
                toast.error(result.message || "Failed to save. Please try again.");
            }
        },
        []
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <svg className="animate-spin w-8 h-8 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" />
                </svg>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Info banner */}
            <div className="rounded-xl border border-blue-100 bg-blue-50/50 px-4 py-3 text-[13px] text-blue-700 leading-relaxed shadow-sm">
                <div className="flex gap-2.5">
                    <div className="mt-0.5">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <p>
                        <span className="font-bold underline decoration-blue-200 underline-offset-2">Company-level settings</span> apply to all programs globally.
                        Local program configurations will always take precedence over these defaults.
                    </p>
                </div>
            </div>

            {/* Cards grid */}
            <div className="grid gap-6 lg:grid-cols-2">
                <SlackCard
                    state={slackState}
                    onChange={setSlackState}
                    webhookConfigured={slackWebhookConfigured}
                    onSave={p => handleSave(p, "slack")}
                    saving={slackSaving}
                />
                <TeamsCard
                    state={teamsState}
                    onChange={setTeamsState}
                    initialConnected={teamsWebhookConfigured}
                    onSave={p => handleSave(p, "teams")}
                    saving={teamsSaving}
                />
                <GitHubCard
                    state={githubState}
                    onChange={setGithubState}
                    tokenSet={githubTokenSet}
                    initialRepo={initialGithubRepo}
                    onSave={p => handleSave(p, "github")}
                    saving={githubSaving}
                />
                <GitLabCard
                    state={gitlabState}
                    onChange={setGitlabState}
                    tokenSet={gitlabTokenSet}
                    initialProject={initialGitLabProject}
                    onSave={p => handleSave(p, "gitlab")}
                    saving={gitlabSaving}
                />
                <JiraCard
                    state={jiraState}
                    onChange={setJiraState}
                    apiTokenSet={apiTokenSet}
                    onSave={p => handleSave(p, "jira")}
                    saving={jiraSaving}
                />
            </div>
        </div>
    );
};

export default CompanyIntegrations;
